import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, HStack, Modal, ModalDialogue, theme } from '@sg/garnish';
import {
  checkIfBaseIngredient,
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { useLocalizationContext } from '@order/Localization';
import {
  telemetryProductFromInput,
  useTrackEventEffect,
} from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationTooFewBasesModal = (props: Props) => {
  const { actor } = props;

  const { t } = useLocalizationContext();

  const { product, activeModifications, shouldShowTooFewBasesModal } =
    useCustomizationContext({ actor });
  const { continueCustomization, endCustomization } = useCustomizationHelpers({
    actor,
  });

  // ─── Derived Data ───────────────────────────────────────────────────────

  const activeBaseCount = useMemo(
    () => activeModifications.filter(checkIfBaseIngredient).length,
    [activeModifications],
  );

  const heading = t('pdp.modifications.too-few-bases-modal.heading', {
    activeBaseCount,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'pdp.view.modal.too-few-bases',
    payload: {
      selectedProduct: product ? telemetryProductFromInput(product) : undefined,
    },
    skip: !shouldShowTooFewBasesModal,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      testID="pdp-modals.too-few-bases"
      visible={shouldShowTooFewBasesModal}
      size="medium"
      accessibilityLabel={heading}
      headline={heading}
      body={t('pdp.modifications.too-few-bases-modal.text')}
      onRequestClose={continueCustomization}
    >
      <SafeAreaView edges={['bottom']}>
        <Modal.Footer withoutTopBorder>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.noDressingCtaWrapper}
          >
            <Button
              size="large"
              palette="secondary"
              onPress={continueCustomization}
              testID="pdp-modals.too-few-bases.go-back"
            >
              {t('general.go-back')}
            </Button>

            <Button
              size="large"
              palette="primary"
              onPress={endCustomization}
              testID="pdp-modals.too-few-bases.continue"
            >
              {t('general.continue')}
            </Button>
          </HStack>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  noDressingCtaWrapper: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
};
